import FormattedNumber from '../_components/formattedNumber';

const isNotPublished = (showFlag, value) =>
  showFlag && (!value || Number.isNaN(Number(value)) || Number(value) === 0);

const RatesTable = ({
  unit,
  parseFloatKeep,
  showLastYearTaxes,
  showNotPublishedForZeroTaxRate,
  isWaterRate,
  isUseTaxElectionRate,
  openWorksheetPDF,
}) => {
  const year = unit.taxingUnitYr;
  const priorYear = Number(year) - 1;
  const isWater = isWaterRate(unit);
  const isElection = isUseTaxElectionRate(unit);
  const isSchool =
    unit.taxingUnitType === 'School' ||
    unit.taxingUnitType === 'School - Chapter 313';

  const revenueColumnHeader =
    isWater && isElection
      ? `${year} Tax Election`
      : `${year} ${isSchool ? 'Rate to Maintain' : 'No New Revenue'}`;

  const revenueRate =
    isWater && isElection ? unit.mandatoryTaxElectionRate : unit.effTaxRate;
  const revenueAmount =
    isWater && isElection
      ? unit.tax_mandatoryTaxElectionRate
      : unit.tax_effTaxRate;
  const revenueS3ID =
    isWater && isElection
      ? unit.mandatoryTaxElectionRateS3ID
      : unit.etrWorksheetS3ID;
  const revenueWorksheetType =
    isWater && isElection ? 'mandatoryTaxElectionRate' : 'effectiveRate';
  const revenueWorksheetLabel =
    isWater && isElection
      ? `${year} Tax Election Worksheet`
      : `${year} No New Revenue Worksheet`;

  const renderNotPublished = () => (
    <span className='not-published'>Not Published</span>
  );

  const renderAmount = (value) => (
    <>
      $
      <FormattedNumber value={parseFloatKeep(value)} />
    </>
  );

  const renderWorksheetLink = (type, label, s3ID) => {
    if (!s3ID) {
      return null;
    }
    return (
      <button
        className='doc-link link-button'
        onClick={() => openWorksheetPDF(type, unit.taxingUnitID)}
        type='button'
      >
        {label}
      </button>
    );
  };

  const hasDifference = Boolean(
    unit.effTaxRate &&
      Number(unit.effTaxRate) &&
      unit.proposedRate &&
      Number(unit.proposedRate) &&
      unit.difference,
  );

  const hasRateDifference = Boolean(
    revenueRate &&
      Number(revenueRate) &&
      unit.proposedRate &&
      Number(unit.proposedRate),
  );

  const computedRateDifference = hasRateDifference
    ? (parseFloat(unit.proposedRate) - parseFloat(revenueRate)).toFixed(6)
    : null;

  return (
    <table aria-label='Tax rates' className='rates-table'>
      <thead>
        <tr>
          <th scope='col' />
          {Boolean(showLastYearTaxes) && (
            <th scope='col'>{priorYear} Tax Rate</th>
          )}
          <th scope='col'>{revenueColumnHeader}</th>
          <th scope='col'>{year} Proposed</th>
          <th scope='col'>Difference</th>
          <th scope='col'>{year} Voter Approval</th>
          <th scope='col'>{year} Adopted</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope='row'>Rate</th>
          {Boolean(showLastYearTaxes) && (
            <td>
              {isNotPublished(
                showNotPublishedForZeroTaxRate,
                unit.prior_adoptedRate,
              )
                ? renderNotPublished()
                : unit.prior_adoptedRate}
            </td>
          )}
          <td>
            {isNotPublished(showNotPublishedForZeroTaxRate, revenueRate)
              ? renderNotPublished()
              : revenueRate}
          </td>
          <td>
            {isNotPublished(showNotPublishedForZeroTaxRate, unit.proposedRate)
              ? renderNotPublished()
              : unit.proposedRate}
          </td>
          <td>
            {hasRateDifference &&
              (Number(computedRateDifference) >= 0 ? (
                computedRateDifference
              ) : (
                <span style={{ color: 'var(--negative-value-color)' }}>
                  ({Math.abs(Number(computedRateDifference)).toFixed(6)})
                </span>
              ))}
          </td>
          <td>
            {isNotPublished(showNotPublishedForZeroTaxRate, unit.rollbackRate)
              ? renderNotPublished()
              : unit.rollbackRate}
          </td>
          <td>
            {isNotPublished(showNotPublishedForZeroTaxRate, unit.adoptedRate)
              ? renderNotPublished()
              : unit.adoptedRate && <span>{unit.adoptedRate}</span>}
          </td>
        </tr>
        <tr>
          <th scope='row'>Amount</th>
          {Boolean(showLastYearTaxes) && (
            <td>
              {isNotPublished(
                showNotPublishedForZeroTaxRate,
                unit.prior_adoptedRate,
              )
                ? renderNotPublished()
                : renderAmount(unit.lastYearTaxes)}
            </td>
          )}
          <td>
            {isNotPublished(showNotPublishedForZeroTaxRate, revenueRate)
              ? renderNotPublished()
              : renderAmount(revenueAmount)}
          </td>
          <td>
            {isNotPublished(showNotPublishedForZeroTaxRate, unit.proposedRate)
              ? renderNotPublished()
              : renderAmount(unit.tax_proposedTaxRate)}
          </td>
          <td>
            {hasDifference &&
              (Number(unit.difference) >= 0 ? (
                renderAmount(unit.difference)
              ) : (
                <span style={{ color: 'var(--negative-value-color)' }}>
                  ($
                  <FormattedNumber
                    value={parseFloatKeep(unit.difference, true)}
                  />
                  )
                </span>
              ))}
          </td>
          <td>
            {isNotPublished(showNotPublishedForZeroTaxRate, unit.rollbackRate)
              ? renderNotPublished()
              : renderAmount(unit.tax_rollbackRate)}
          </td>
          <td>
            {isNotPublished(showNotPublishedForZeroTaxRate, unit.adoptedRate)
              ? renderNotPublished()
              : unit.tax_adoptedRate && (
                  <span>{renderAmount(unit.tax_adoptedRate)}</span>
                )}
          </td>
        </tr>
        <tr>
          <th scope='row'>Worksheet</th>
          {Boolean(showLastYearTaxes) && <td />}
          <td>
            {renderWorksheetLink(
              revenueWorksheetType,
              revenueWorksheetLabel,
              revenueS3ID,
            )}
          </td>
          <td>
            {renderWorksheetLink(
              'proposedRate',
              'Worksheet',
              unit.taxRateProposedS3ID,
            )}
          </td>
          <td />
          <td>
            {renderWorksheetLink(
              'rollbackRate',
              'Worksheet',
              unit.taxRateVoterApprovalS3ID,
            )}
          </td>
          <td>
            {renderWorksheetLink(
              'adoptedRate',
              'Adopted Tax Rate Statement',
              unit.taxRateAdoptionS3ID,
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default RatesTable;
